/**
 * The arithmetic behind the photo cropper, with no DOM in it.
 *
 * A cropper is two things: a picture somebody drags around inside a square, and
 * the sum that turns where they left it into the pixels to keep. The second is
 * the part that goes wrong — off by a factor of the zoom, or a rectangle that
 * reaches past the edge of the image and comes back with transparent bands —
 * and it is the part a test can reach. It lives here so it can be one.
 *
 * This is also why there is no cropping library in the admin's bundle. What a
 * library would add is a picture positioned inside a square with
 * `overflow: hidden`, and this file; the picture is six lines of CSS.
 */

export interface Size {
  width: number;
  height: number;
}

/** Where the picture's top-left sits, relative to the viewport's, in screen pixels. */
export interface Offset {
  x: number;
  y: number;
}

/** The square of the original to keep, in the original's own pixels. */
export interface SourceRect {
  x: number;
  y: number;
  size: number;
}

/**
 * The scale at which the picture just covers the square.
 *
 * `max`, not `min`: at `min` the picture fits *inside* the square and leaves
 * bands of nothing down two sides, which then get cropped into the photo.
 * Covering is the floor the zoom starts from, and no zoom below it is offered.
 */
export const coverScale = (natural: Size, viewport: number): number => {
  if (natural.width <= 0 || natural.height <= 0) return 1;
  return Math.max(viewport / natural.width, viewport / natural.height);
};

/** How big the picture is on screen at this zoom. */
export const displayedSize = (natural: Size, viewport: number, zoom: number): Size => {
  const scale = coverScale(natural, viewport) * zoom;
  return { width: natural.width * scale, height: natural.height * scale };
};

/**
 * The offset, held inside the range that keeps the square covered.
 *
 * Dragging is free until an edge would come into view, and then it stops. The
 * alternative is letting somebody drag the picture halfway out of the frame and
 * saving a photo that is half background — which nobody means to do, and which
 * the preview would have to show as empty space to be honest about.
 */
export const clampOffset = (
  offset: Offset,
  natural: Size,
  viewport: number,
  zoom: number,
): Offset => {
  const shown = displayedSize(natural, viewport, zoom);
  const within = (value: number, length: number): number =>
    // `length` can be a rounding hair under `viewport`, which would put the
    // minimum above the maximum and pin the picture to a corner.
    Math.min(0, Math.max(Math.min(0, viewport - length), value));

  return { x: within(offset.x, shown.width), y: within(offset.y, shown.height) };
};

/**
 * The square of the original that the viewport is showing.
 *
 * Everything above is in screen pixels; this converts back, because the canvas
 * has to read from the file rather than from the preview — a preview is at
 * whatever size the panel happened to be, and the photo that gets stored should
 * not be.
 */
export const sourceRect = (
  natural: Size,
  viewport: number,
  zoom: number,
  offset: Offset,
): SourceRect => {
  const scale = coverScale(natural, viewport) * zoom;
  const held = clampOffset(offset, natural, viewport, zoom);

  return { x: -held.x / scale, y: -held.y / scale, size: viewport / scale };
};

/**
 * The offset that centres the picture at this zoom.
 *
 * Where a photo opens, and where it goes back to when the zoom changes under
 * it: growing the picture around its top-left corner would slide whatever
 * somebody had framed off to one side.
 */
export const centredOffset = (natural: Size, viewport: number, zoom: number): Offset => {
  const shown = displayedSize(natural, viewport, zoom);
  return { x: (viewport - shown.width) / 2, y: (viewport - shown.height) / 2 };
};

/**
 * Keeps the middle of the square still while the zoom changes.
 *
 * Zooming around the corner is the version that feels broken: the face
 * somebody has just centred walks out of the frame as the slider moves, and
 * they have to drag it back after every nudge.
 */
export const zoomedOffset = (
  offset: Offset,
  natural: Size,
  viewport: number,
  from: number,
  to: number,
): Offset => {
  const middle = viewport / 2;
  const ratio = to / from;
  const held = clampOffset(offset, natural, viewport, from);

  return clampOffset(
    { x: middle - (middle - held.x) * ratio, y: middle - (middle - held.y) * ratio },
    natural,
    viewport,
    to,
  );
};
